<!--Owner: Web 1 team. If You want to make changes, please contact us and add to review-->

<template>
  <div
    v-click-outside="hideMultiSelectItems"
    class="multiselect-box"
  >
    <button
      class="multiselect-box__select"
      :disabled="disabled"
      :class="{
        'multiselect-box__select--disabled': disabled,
        'multiselect-box__select--active': selectActive && !errorMessage,
        'multiselect-box__select--error': errorMessage,
        'multiselect-box__select--none-label': selectedItemsLabel === labelStrings.noneLabel,
      }"
      :style="{
        'max-width': maxWidth ? `${maxWidth}px` : 'auto',
      }"
      type="button"
      @click="selectActive = !selectActive"
    >
      <tooltipped-text :text="selectedItemsLabel" />
      <v-icon
        v-show="!errorMessage"
        :size="24"
        class="ml-auto"
        :color="disabled ? 'secondaryLight' : 'secondaryMedium'"
      >
        $icon_arrow_down
      </v-icon>
      <v-icon
        v-show="errorMessage"
        :size="24"
        color="negative"
        class="ml-auto"
      >
        $icon_attention
      </v-icon>
      <v-progress-linear
        v-if="loading"
        absolute
        bottom
        indeterminate
        rounded
        height="2"
        :color="loaderColor"
      />
    </button>
    <div
      v-show="selectActive"
      class="multiselect-box__content"
    >
      <div class="mb-2">
        <SearchBar
          v-model="currentSearch"
          :collections="[]"
          :show-collection="false"
          :placeholder="labelStrings.searchPlaceholder ? labelStrings.searchPlaceholder : ''"
          @input="emitSearchDebounced"
        />
      </div>
      <div class="multiselect-box__items">
        <div class="multiselect-box__item--normal">
          <CheckBox
            v-if="!disableSelectAll"
            v-model="isSelectedAll"
            :label="labelStrings.allLabel"
            :indeterminate="isIndeterminate"
            :force-active="forceActive"
            class="multiselect-box__item__checkbox"
            @input="emitSelectAll()"
          />
        </div>
        <div>
          <div
            v-for="item in selectedItems"
            v-show="selectedItems"
            :key="`${item.text}${item.value}`"
            class="multiselect-box__item--normal"
          >
            <CheckBox
              :label="item.text"
              :value="true"
              class="multiselect-box__item__checkbox"
              :label-max-width="labelMaxWidth"
              @change="clickItem(item)"
            />
          </div>
          <v-divider
            v-show="isIndeterminate"
            class="my-1 mr-1"
          />
          <div v-if="searchedAPIResults?.length">
            <div
              v-for="item in searchedAPIResults"
              :key="`${item.text}${item.value}`"
              class="multiselect-box__item--searched"
            >
              <CheckBox
                :label="item.text"
                :value="false"
                class="multiselect-box__item__checkbox"
                :label-max-width="labelMaxWidth"
                @change="clickSearchedItem(item)"
              />
            </div>
          </div>
        </div>
        <div
          v-if="currentSearch && !searchedAPIResults?.length && paginationRef.completedInitialSearch"
          class="multiselect-box__no-results"
        >
          <p>{{ labelStrings.noResultsLabel }}</p>
        </div>
        <div v-if="(selectedItems.length !== originalItems.length && paginationRef.completedSearch) || !currentSearch">
          <div
            v-for="collection of availableCollections"
            :key="collection.name"
          >
            <p
              v-if="collection.items.length > 0 && collections.length > 1"
              class="multiselect-box__category"
            >
              {{ collection.name }}
            </p>
            <div
              v-for="item in collection.items"
              :key="`${item.text}${item.value}`"
              class="multiselect-box__item--normal"
              @click="clickItem(item)"
            >
              <CheckBox
                :label="item.text"
                :value="selectedItems.some((selectedItem) => selectedItem.value === item.value)"
                class="multiselect-box__item__checkbox"
                :label-max-width="labelMaxWidth"
                @change="clickItem(item)"
              />
            </div>
          </div>
        </div>
        <div v-intersect="handleIntersection" />
        <loading-spinner
          v-if="!paginationRef.loaded"
          is-loading
          :size="20"
          color="primary"
          :absolute="false"
          :class="['infinite-loader', { 'infinite-loader--margin': !collections.length }]"
        />
      </div>
    </div>
    <div
      v-show="showErrorMessage"
      class="multiselect-box__error-message"
    >
      {{ errorMessage || errorBucket[0] }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import TooltippedText from '../../tooltip/TooltippedText/TooltippedText.vue';
import SearchBar from '../../text-inputs/searchBar/SearchBar.vue';
import CheckBox from '../../selection-controls/CheckBox.vue';
import LoadingSpinner from '../../loaders/LoadingSpinner.vue';
import _ from 'lodash';

import { Collection, SelectBoxItem, LabelStrings } from '../types';
import { clickItemHandler, selectedItemsLabelHandler, clickSearchItemHandler } from '../multiSelectUtils';
import { MultipleIntersectionPaginationOption, MultipleIntersectionStateChanger } from '@/utils/types';

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    value?: SelectBoxItem[];
    maxWidth?: number;
    collections: Collection[];
    labelStrings: LabelStrings;
    errorMessage?: string;
    labelMaxWidth?: string | number;
    loading?: boolean;
    searchAPIResults: SelectBoxItem[];
    rules?: Array<string | boolean | ((value: object) => boolean | string)>;
    disableSelectAll?: boolean;
  }>(),
  {
    labelMaxWidth: '100%',
    errorMessage: '',
    loading: false,
    disableSelectAll: false,
  }
);

const emit = defineEmits<{
  (e: 'input', value?: SelectBoxItem[]): void;
  (e: 'searchInput', value?: SelectBoxItem[]): void;
  (
    e: 'search',
    value: {
      searchText: string | undefined | null;
      state: MultipleIntersectionStateChanger;
    }
  ): void;
  (e: 'infinite', value: MultipleIntersectionStateChanger): void;
  (e: 'selectAll', value: MultipleIntersectionStateChanger): void;
  (e: 'clickItem', value: SelectBoxItem): void;
}>();

// pagination
const paginationRef = ref<MultipleIntersectionPaginationOption>({
  loaded: false,
  completedSearch: false,
  completedInitialSearch: false,
  completedAvailable: false,
  bounceTimer: undefined,
  totalAvailableItemsSize: 0,
});

const forceActive = ref(false);
const loaderColor = computed(() => (props.errorMessage ? 'negative' : 'secondaryMedium'));
const isDirty = ref<boolean>(false);
const valid = ref<boolean>(false);
const errorBucket = ref<string[]>([]);
// CONVERTED PROPS
const originalItems = computed(() => props.collections.flatMap((collection: Collection) => collection.items));
const inRange = (num: number, a: number, b = 0) => Math.min(a, b) <= num && num < Math.max(a, b);
const isIndeterminate = ref(
  !!paginationRef.value.totalAvailableItemsSize &&
    props?.value &&
    inRange(props?.value?.length, 1, paginationRef.value.totalAvailableItemsSize)
);

const showErrorMessage = computed(() => {
  if (props.rules?.length) {
    return isDirty.value && !valid.value;
  } else {
    return !!props.errorMessage;
  }
});

const searchedAPIResults = computed({
  get: () =>
    props.searchAPIResults
      ? props.searchAPIResults.filter((item) => !selectedItems.value.some((value) => value.value === item.value))
      : [],
  set: (newValue) => {
    emit('searchInput', newValue);
  },
});
// SELECTED ITEMS
const selectedItems = computed({
  get: () => (props.value ? props.value : []),
  set: (newValue) => {
    isIndeterminate.value = inRange(newValue.length, 1, paginationRef.value.totalAvailableItemsSize);
    emit('input', newValue);
    validate(newValue);
  },
});

// AVAILABLE ITEMS
const availableCollections = computed(() => {
  const searchAPIResult = props.searchAPIResults || [];
  return props.value
    ? props.collections.map((collection: Collection) => {
        const items = [...selectedItems.value, ...searchAPIResult];

        return {
          name: collection.name,
          items: collection.items.filter((item) => !items.some((value) => value.value === item.value)),
        };
      })
    : props.collections;
});

const isSelectedAll = computed({
  get: () =>
    !!paginationRef.value.totalAvailableItemsSize &&
    selectedItems.value.length === paginationRef.value.totalAvailableItemsSize,
  // ommit vue warning about set function
  set: (newValue) => {
    return newValue;
  },
});

// SELECT ALL
const selectActive = ref<boolean>(false);

const currentSearch = ref<string>('');
// LABEL
const selectedItemsLabel = computed(() =>
  selectedItemsLabelHandler(isSelectedAll.value, selectedItems.value, props.labelStrings)
);

// EVENT HANDLERS
const hideMultiSelectItems = () => {
  selectActive.value = false;
};

// Function responsible for removing/adding items allFilteredItems
const clickItem = (item: SelectBoxItem) => {
  selectedItems.value = clickItemHandler(item, selectedItems.value);
};

const clickSearchedItem = (item: SelectBoxItem) => {
  searchedAPIResults.value = clickSearchItemHandler(item, searchedAPIResults.value);
  selectedItems.value = clickItemHandler(item, selectedItems.value);
};

watch(selectActive, (newValue, oldValue) => {
  if (!newValue && newValue !== oldValue) {
    currentSearch.value = '';
  }
});

const handleIntersection = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
  isIntersecting: boolean
) => {
  if (isIntersecting) {
    if (
      paginationRef.value.completedAvailable ||
      (currentSearch.value && !paginationRef.value.completedInitialSearch)
    ) {
      return;
    }
    paginationRef.value.loaded = false;
    emit('infinite', state);
  }
};

const state: MultipleIntersectionStateChanger = {
  loaded: () => {
    paginationRef.value.loaded = true;
  },
  completedInitialSearch: () => {
    paginationRef.value.completedInitialSearch = true;
  },
  completedSearch: () => {
    paginationRef.value.completedSearch = true;
  },
  completedAvailable: () => {
    paginationRef.value.completedAvailable = true;
  },
  setAvailableItemsSize: (items: number) => {
    paginationRef.value.totalAvailableItemsSize = items;
  },
  reset: () => {
    paginationRef.value.loaded = false;
    paginationRef.value.completedInitialSearch = false;
    paginationRef.value.completedSearch = false;
  },
  resetCompletedAvailable: () => {
    paginationRef.value.completedAvailable = false;
  },
};

const emitSearch = () => {
  emit('search', { searchText: currentSearch.value, state });
};

const emitSearchDebounced = _.debounce(emitSearch, 600);

const emitSelectAll = () => {
  const nextIsSelectedAll = !isSelectedAll.value;
  forceActive.value = !forceActive.value;
  if (!paginationRef.value.completedAvailable) {
    if (nextIsSelectedAll) {
      emit('selectAll', state);
    }
  } else {
    selectedItems.value = nextIsSelectedAll ? originalItems.value : [];
  }
};

const validate = (newValue: SelectBoxItem[]) => {
  isDirty.value = true;

  if (!props.rules?.length) {
    return;
  }

  const value = newValue || props?.value;
  const errorBucketTemp = [];

  for (let index = 0; index < props.rules.length; index++) {
    const rule = props.rules[index];
    const valid = typeof rule === 'function' ? rule(value) : rule;

    if (valid === false || typeof valid === 'string') {
      errorBucketTemp.push(valid || '');
    }
  }

  errorBucket.value = errorBucketTemp;
  valid.value = errorBucketTemp.length === 0;

  return valid.value;
};

const resetSelectAll = () => {
  isIndeterminate.value = false;
  forceActive.value = false;
};

defineExpose({ resetSelectAll });
</script>

<style lang="scss" scoped>
@import '../../../assets/styles/main';
@import '../multiSelectStyles';
.multiselect-box {
  &__select {
    position: relative;
  }

  &__item {
    height: 28px;
    &__checkbox {
      padding-top: 2px !important;
    }
    &--normal {
      &:hover {
        background: $accentClear;
        border-radius: 4px;
        margin-right: 4px;
      }
    }
    &--searched {
      background: $accentClear;
      border-radius: 4px;
      margin-right: 4px;
    }
  }
  &__searched-items {
    background: $accentClear;
    box-shadow: none;
    border-radius: 4px;
  }

  &__category {
    @include body-3;
    color: $secondaryMedium;
    margin: 4px 0 !important;
  }

  &__no-results {
    display: flex;
    align-items: center;
    height: 44px;
    p {
      @include body-2;
      margin: 0 !important;
    }
  }
}
</style>
